import './form-searchable-select-field.scss';

import { Box, FilterOptionsState, TextField, styled } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import {
  CountryDetails,
  IndustrySectorDetails,
  UploadFolder,
  useDebounce,
  useGetBrokerOptions,
  useGetCountryQuery,
  useGetIndustrySectorsQuery,
  useUploadFoldersQuery,
} from '@converge/graphql';
import { Dispatch, SetStateAction, useState } from 'react';
import { FocusEvent, ReactNode, useEffect, useMemo } from 'react';
import { Region, useGetRegionsQuery } from '@converge/graphql';

import {
  ApplicationSelectOptions,
  NAICS_PLACEHOLDER_CODE,
} from '@converge/data';
import Autocomplete from '@mui/material/Autocomplete';
import { FormFieldContainer } from './form-field-container';
import { NewConvergeInputLabel } from './new-converge-input-label';
import { asCase } from '@converge/utils';
import { validate as uuidValidate } from 'uuid';

interface ListOption<D, V = string | number> {
  value: V;
  label: string;
  data: D;
}

interface FormSearchableSelectFieldProps<D = unknown, V = string | number> {
  name: string;
  containerWidth?: number;
  options?: D[];
  onBlur?: () => void;
  mapToOption?: (
    item: D
  ) => Omit<ListOption<D, V>, 'data'> & Partial<Pick<ListOption<D, V>, 'data'>>;
  isLoading?: boolean;
  onChangeOption?: (item: ListOption<D, V>) => void;
  hideLabel?: boolean;
  label?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  resetAfterSelect?: boolean;
  placeholder?: string;
  minDropdownWidth?: string;
  maxDropdownWidth?: string;
  value?: V;
  noOptionsText?: string;
  disableClearable?: boolean;
  renderOptionStyle?: React.CSSProperties;
  preProcess?: (
    selectedItem: ListOption<D, V>,
    defaultValue: V,
    setValue: (value: V) => void
  ) => void;
  filterOptions?: (
    options: ListOption<D, V>[],
    state: FilterOptionsState<ListOption<D, V>>
  ) => ListOption<D, V>[];
  autoComplete?: boolean;
  clearOnBlur?: boolean;
  setSearchText?: (value: string) => void;
}

interface PreConfiguredFormSearchableSelectFieldProps<D = unknown, V = string>
  extends Omit<
    FormSearchableSelectFieldProps<D, V>,
    'options' | 'mapToOption' | 'isLoading'
  > {}

interface PreConfiguredStaticFormSearchableSelectFieldProps<D = unknown>
  extends PreConfiguredFormSearchableSelectFieldProps<D> {
  optionsType: SearchableOptionsDataType;
}

export type SearchableOptionsDataType =
  keyof typeof ApplicationSelectOptions.optionsData;

interface BrokerFormSearchableSelectFieldProps<D = unknown, V = string>
  extends Omit<FormSearchableSelectFieldProps<D, V>, 'defaultValue'> {
  brokerageUuid: string;
  defaultValue?: string;
}

export const FormSearchableSelectField = <D, V = string>({
  name,
  label = name,
  required,
  containerWidth = 6,
  options,
  mapToOption,
  isLoading,
  onChangeOption,
  hideLabel = false,
  preProcess,
  value: valueProp,
  resetAfterSelect,
  placeholder,
  autoComplete,
  noOptionsText,
  onBlur,
  disableClearable = false,
  clearOnBlur,
  setSearchText,
  minDropdownWidth = '500px',
  maxDropdownWidth = '600px',
  ...otherProps
}: FormSearchableSelectFieldProps<D, V>) => {
  const { control, setValue, clearErrors, getValues } = useFormContext();
  const [hasClearedForThisSelection, setHasClearedForThisSelection] =
    useState(false);

  const listOptions = useMemo((): ListOption<D, V>[] => {
    if (isLoading) {
      return [];
    }

    if (!options?.length) {
      return [];
    }

    if (mapToOption) {
      return options.map((item) => {
        const option = mapToOption(item);
        option.data = item;
        return option as ListOption<D, V>;
      });
    }

    return options as ListOption<D, V>[];
  }, [isLoading, mapToOption, options]);

  useEffect(() => {
    const defaultValue = getValues(name);
    const defaultSelectedOption = listOptions.find(
      (option) => option.value === defaultValue || option.label === defaultValue
    );
    if (preProcess && defaultSelectedOption) {
      preProcess(defaultSelectedOption || null, defaultValue, (value: V) =>
        setValue(name, value)
      );
    }
  }, [preProcess, setValue, getValues, name, listOptions]);

  const StyledSearchableTextField = useMemo(
    () => styled(TextField)`
      .MuiOutlinedInput-root {
        font-size: 14px !important;
        font-weight: 600 !important;
        height: 40px !important;
        .MuiOutlinedInput-notchedOutline {
          height: 40px !important;
          border: 1px solid #4d4d4d !important;
        }
        &.Mui-error .MuiOutlinedInput-notchedOutline {
          border: 1px solid red !important;
        }
        &.Mui-disabled .MuiOutlinedInput-notchedOutline {
          border: 1px solid #bfbdbd !important;
        }

        &.Mui-focused .MuiOutlinedInput-notchedOutline {
          border: 1px solid #23a892 !important;
        }

        &:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline {
          border: 1px solid #23a892 !important;
        }
      }

      input::placeholder {
        font-size: 14px !important;
        font-weight: 600 !important;
      }

      .MuiAutocomplete-clearIndicator {
        color: rgba(0, 0, 0, 0.54);
        &:hover {
          color: rgba(0, 0, 0, 0.87);
        }
      }
    `,
    []
  );

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required,
      }}
      render={({
        field: { ref, onChange, value, onBlur: formOnBlur, ...otherField },
        fieldState: { error },
      }) => {
        const selectedListOption = (value: string) => {
          return listOptions.find(
            (option) => option.value === value || option.label === value
          );
        };
        const valueToRender = resetAfterSelect
          ? null
          : selectedListOption(valueProp ?? value) ?? null;
        return (
          <FormFieldContainer
            md={containerWidth}
            className={error?.message ? 'error-container' : ''}
          >
            {hideLabel === false && (
              <NewConvergeInputLabel
                label={label}
                required={required}
                className={error?.message ? 'error-message' : ''}
              />
            )}
            <Autocomplete
              sx={{
                '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator':
                  {
                    transform: 'translateY(3.1px)',
                    color: 'black !important',
                  },
                '&.Mui-disabled .MuiAutocomplete-popupIndicator, &.Mui-disabled .MuiAutocomplete-clearIndicator':
                  {
                    color: '#bfbdbd !important',
                  },
                '& + .MuiAutocomplete-popper li[aria-selected="true"]': {
                  backgroundColor: '#cefcf6 !important',
                },
                '& + .MuiAutocomplete-popper li.Mui-focused': {
                  backgroundColor: '#e7fdfb !important',
                },
                '& + .MuiAutocomplete-popper li:hover': {
                  backgroundColor: '#e7fdfb !important',
                },
              }}
              loading={isLoading}
              value={valueToRender}
              disableClearable={disableClearable}
              clearOnBlur={clearOnBlur}
              options={listOptions || []}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value || option.label === value.value
              }
              renderOption={(props, option) => {
                // Extract className and style from props to handle them separately
                const { className, style, ...otherProps } = props;

                return (
                  <li
                    className={className}
                    style={{
                      ...style,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: 1.2,
                      padding: '8px 16px',
                      display: 'block',
                      width: '100%',
                      overflow: 'visible',
                    }}
                    {...otherProps}
                  >
                    <div
                      style={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        width: '100%',
                        maxWidth: '100%',
                      }}
                    >
                      {option.label}
                    </div>
                  </li>
                );
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input' && setSearchText) {
                  const currentValue = getValues(name);
                  // first time typing after a selection: wipe out the old form field value
                  if (!hasClearedForThisSelection) {
                    if (currentValue) {
                      setValue(name, '');
                    }
                    setHasClearedForThisSelection(true);
                  }
                  setSearchText(newInputValue);
                }
              }}
              onChange={(e, option) => {
                setValue(name, option?.value || null);
                setHasClearedForThisSelection(false);
                clearErrors(name);
                if (onChangeOption) {
                  onChangeOption(option as ListOption<D, V>);
                }
                if (
                  document.activeElement &&
                  'blur' in document.activeElement &&
                  typeof document.activeElement.blur === 'function'
                ) {
                  document.activeElement.blur();
                }
              }}
              noOptionsText={noOptionsText}
              renderInput={(params) => (
                <StyledSearchableTextField
                  {...params}
                  placeholder={placeholder}
                  inputProps={{
                    ...params.inputProps,
                    onBlur: (event) => {
                      if (onBlur) {
                        onBlur();
                      }
                      if (params.inputProps.onBlur) {
                        params.inputProps.onBlur(
                          event as FocusEvent<HTMLInputElement>
                        );
                      }
                    },
                    autoComplete:
                      typeof autoComplete === 'boolean'
                        ? autoComplete
                          ? 'on'
                          : 'off'
                        : undefined,
                  }}
                  className="styled-searchable-text-field"
                  error={!!error}
                  variant="outlined"
                  size="small"
                  helperText={
                    error ? (
                      <Box
                        style={{
                          fontWeight: 600,
                        }}
                      >
                        {error.message as string}
                      </Box>
                    ) : (
                      ''
                    )
                  }
                  data-testid="form-searchable-select-input"
                  name={name}
                />
              )}
              className="form-autoComplete"
              {...otherField}
              {...otherProps}
            />
          </FormFieldContainer>
        );
      }}
    />
  );
};

export const BrokerFormSearchableSelectField = (
  props: BrokerFormSearchableSelectFieldProps<CountryDetails>
) => {
  const { brokerageUuid } = props;
  const [searchText, setSearchText] = useState('');
  const debouncedAgentName = useDebounce<string>(searchText, 500);

  const queryOptions = useMemo(
    () => ({
      pageNumber: 0,
      pageSize: 25,
      firstName: debouncedAgentName.length > 1 ? debouncedAgentName : '',
      enabled: true,
      brokerageUuid,
    }),
    [brokerageUuid, debouncedAgentName]
  );
  const { brokers, loading } = useGetBrokerOptions(queryOptions);

  return (
    <FormSearchableSelectField
      minDropdownWidth="530px"
      maxDropdownWidth="530px"
      {...props}
      options={brokers?.sort((a, b) => a.label.localeCompare(b.label))}
      clearOnBlur={false}
      isLoading={loading}
      mapToOption={(broker) => {
        return {
          value: broker.uuid ?? '',
          label: broker.name ?? 'N/A',
        };
      }}
      preProcess={(selectedItem, defaultValue, setValue) => {
        if (!uuidValidate(defaultValue) && selectedItem) {
          setValue(selectedItem.value);
        }
      }}
      setSearchText={(value) => setSearchText(value)}
    />
  );
};

export const CountryFormSearchableSelectField = (
  props: PreConfiguredFormSearchableSelectFieldProps<CountryDetails>
) => {
  const { loading, countries } = useGetCountryQuery();

  return (
    <FormSearchableSelectField<CountryDetails>
      minDropdownWidth={props.minDropdownWidth}
      maxDropdownWidth={props.maxDropdownWidth}
      {...props}
      options={countries}
      isLoading={loading}
      mapToOption={(country) => {
        return {
          value: country.uuid ?? '',
          label: country.name ?? 'N/A',
        };
      }}
      preProcess={(selectedItem, defaultValue, setValue) => {
        if (!uuidValidate(defaultValue) && selectedItem) {
          setValue(selectedItem.value);
        }
      }}
    />
  );
};

export const RegionFormSearchableSelectField = (
  props: PreConfiguredFormSearchableSelectFieldProps<Region> & {
    selectedCountry: string;

    setRegionName: Dispatch<SetStateAction<string>>;
  }
) => {
  const countryCode = { countryCode: props.selectedCountry || 'US' };
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions

  const { loading, regions } = useGetRegionsQuery(countryCode);
  return (
    <FormSearchableSelectField<Region>
      {...props}
      minDropdownWidth={props.minDropdownWidth}
      maxDropdownWidth={props.maxDropdownWidth}
      options={regions}
      isLoading={loading}
      mapToOption={(region) => {
        props.setRegionName(region.type || '');
        return {
          value: region.uuid ?? '',
          label: region.name ?? 'N/A',
        };
      }}
      preProcess={(selectedItem, defaultValue, setValue) => {
        if (!uuidValidate(defaultValue) && selectedItem) {
          setValue(selectedItem.value);
        }
      }}
    />
  );
};

export const IndustrySectorFormSearchableSelectField = (
  props: PreConfiguredFormSearchableSelectFieldProps<IndustrySectorDetails>
) => {
  const { loading, industrySectors } = useGetIndustrySectorsQuery();
  const selectableSectors = (industrySectors ?? []).filter(
    (sector) => sector.naicsCode !== NAICS_PLACEHOLDER_CODE
  );
  const value =
    props.value === (NAICS_PLACEHOLDER_CODE as unknown as string | number)
      ? undefined
      : props.value;
  return (
    <FormSearchableSelectField<IndustrySectorDetails>
      {...props}
      value={value}
      options={selectableSectors}
      isLoading={loading}
      minDropdownWidth={props.minDropdownWidth}
      maxDropdownWidth={props.maxDropdownWidth}
      mapToOption={(industrySector) => {
        return {
          value: (industrySector.naicsCode ?? -1) as unknown as string,
          label: `${industrySector.name} (${industrySector.naicsCode})`,
        };
      }}
    />
  );
};

export const UploadFolderFormSearchableSelectField = (
  props: PreConfiguredFormSearchableSelectFieldProps<UploadFolder>
) => {
  const { loading, uploadFolders } = useUploadFoldersQuery();
  return (
    <FormSearchableSelectField<UploadFolder>
      minDropdownWidth={props.minDropdownWidth}
      maxDropdownWidth={props.maxDropdownWidth}
      {...props}
      options={uploadFolders}
      isLoading={loading}
      mapToOption={(uploadFolder) => {
        return {
          value: uploadFolder.name,
          label: asCase(uploadFolder.name),
        };
      }}
    />
  );
};

export const StaticFormSearchableSelectField = <D,>({
  optionsType,
  ...props
}: PreConfiguredStaticFormSearchableSelectFieldProps<ListOption<D>>) => {
  const data = ApplicationSelectOptions.optionsData[optionsType]?.map(
    (item): ListOption<D> => {
      return {
        label: item.name,
        value: item.uuid,
        data: item as D,
      };
    }
  );

  return <FormSearchableSelectField<ListOption<D>> {...props} options={data} />;
};
